/*
 * FORMA — STORE CONTENT (single source of truth)
 * ------------------------------------------------------------------
 * Every piece of text, price, image and checkout link on the website
 * comes from this file. Edit it and every page updates automatically.
 *
 * HOW TO EDIT
 * - `site`      : brand name, hero/about copy, contact email, Instagram,
 *                 currency symbol, FAQ and testimonials.
 *                 Leave `instagramUrl` empty ("") to hide the icon.
 *                 Testimonials only appear when the array has entries
 *                 (see the commented example next to `testimonials`).
 * - `products`  : one object per planner. `slug` is used in the URL
 *                 (/products/<slug>). `gallery` can hold any number of
 *                 image URLs (the first is shown first).
 * - `variants`  : price option(s) for a product. With ONE variant the site
 *                 just shows the price. Add 2+ variants and a package
 *                 selector appears automatically (the FIRST is the default;
 *                 link one directly with /products/<slug>?variant=<id>).
 *                 `price` is a number (no symbol).
 * - `whopUrl`   : PASTE EACH WHOP CHECKOUT LINK HERE (inside the quotes).
 *                 Leave it empty ("") to show a disabled "Coming Soon".
 * - `site.refundPolicy` : content of the /refund-policy page.
 * - The contact email is set once in CONTACT_EMAIL below.
 */

const IMG = "https://static.prod-images.emergentagent.com/jobs/4445e508-0638-425c-a519-69d361cf257d/images";
const CONTACT_EMAIL = "customerservice@formafitnesscoaching.com";
const PDF_FORMATS = ["PDF (instant download)", "Works on phone, tablet & computer", "Printable"];

export const images = {
  hero: `${IMG}/cb902db5efe4b8ad3e25bbaf0c01c52058bc081341cd55cc792237af57b0cecc.jpeg`,
  about: `${IMG}/d05722f4ed15a89c85e3564579d312437bddd9d11989b8c3371ca63535d9a138.jpeg`,
};

export const site = {
  brandName: "FORMA",
  tagline: "Digital planners for a disciplined life.",
  heroHeadline: "Structure for the body you're building.",
  heroSubheadline:
    "Beautifully designed digital fitness planners for training, nutrition and progress. Download instantly, use on any tablet or print at home.",
  aboutTitle: "Designed for consistency, not chaos.",
  aboutText:
    "FORMA was born from a simple belief: progress comes from showing up, and showing up is easier with a clear plan. Our planners strip away the noise and give you calm, considered pages to plan your training, your meals and your growth — so you can focus on the work itself.",
  instagramUrl: "",
  contactEmail: CONTACT_EMAIL,
  currencySymbol: "$",
  faq: [
    {
      q: "What format are the products in?",
      a: "Every product is a PDF, delivered as an instant download. Open it on your phone, tablet or computer, or print it at home.",
    },
    {
      q: "How do I receive my purchase?",
      a: "Checkout is handled securely by Whop. Right after payment you get an instant download link, and a copy is sent to the email you used at checkout.",
    },
    {
      q: "Which devices can I use, and can I print it?",
      a: "Any phone, tablet or computer that can open a PDF. Prefer paper? Print the pages you need at home or at a local print shop.",
    },
    {
      q: "What is your refund policy?",
      a: "Because our products are digital and delivered instantly, all sales are final. We will refund or fix your order if you were charged twice, never received your file, or received a damaged file we can't replace.",
      link: { label: "Read the full refund policy", to: "/refund-policy" },
    },
    {
      q: "How can I contact you?",
      a: `Email us at ${CONTACT_EMAIL} and we'll get back to you within 1–2 business days.`,
    },
  ],
  refundPolicy: {
    title: "Refund Policy",
    lastUpdated: "Last updated: October 2026",
    sections: [
      {
        heading: "Digital products",
        paragraphs: [
          "All FORMA products are digital files delivered instantly after purchase. Because the file can't be returned once it has been delivered, all sales are final and we do not offer refunds for change of mind.",
        ],
      },
      {
        heading: "When we will refund or fix your order",
        bullets: [
          "You were charged twice for the same product.",
          "You never received your download link (after checking your spam folder) and we can't resend it within 3 business days.",
          "Your file is damaged or won't open and we can't provide a working copy within 3 business days.",
        ],
      },
      {
        heading: "How to request a refund",
        paragraphs: [
          `Email us at ${CONTACT_EMAIL} within 14 days of purchase. Please include the email address you used at checkout and your Whop order or receipt number.`,
        ],
      },
      {
        heading: "How refunds are issued",
        paragraphs: [
          "Approved refunds are issued through Whop to your original payment method. Depending on your bank, it typically takes 5–10 business days for the refund to appear.",
        ],
      },
      {
        heading: "Chargebacks",
        paragraphs: [
          "If there's a problem with your order, please contact us first. We'll do our best to resolve it quickly, without the need for a chargeback.",
        ],
      },
    ],
  },
  // TESTIMONIALS — real customers only. Section is hidden while this is empty.
  // Format (copy, uncomment and fill in; images are optional — before/after
  // photos only show when BOTH are provided):
  // {
  //   name: "Sarah K.",                      // first name + initial
  //   productSlug: "mygympal",               // mygympal | 4-week-fat-loss-ebook | ultimate-fitness-coaching
  //   rating: 5,                             // 1–5 stars
  //   quote: "What the customer said...",
  //   beforeImage: "https://...",            // optional
  //   afterImage: "https://...",             // optional
  // },
  testimonials: [],
};

export const products = [
  {
    slug: "mygympal",
    name: "MyGymPal",
    tagline: "Your gym companion, on every session.",
    description:
      "A downloadable gym companion that brings structure to every visit. Plan your sessions, log sets, reps and weights, and keep your training consistent — all in a clean, distraction-free layout you can use on any device or print.",
    coverImage: `${IMG}/ca3d29ab2450b05e2d6e53372a2a155402e9554f343db4cb6e04b40ca7881740.jpeg`,
    gallery: [`${IMG}/ca3d29ab2450b05e2d6e53372a2a155402e9554f343db4cb6e04b40ca7881740.jpeg`],
    formats: PDF_FORMATS,
    highlights: [
      "Undated weekly gym schedules",
      "Session logs for sets, reps, weight and rest",
      "Personal records and goal pages",
      "Hyperlinked tabs for fast navigation",
    ],
    variants: [
      {
        id: "standard",
        name: "Full Access",
        price: 18,
        description: "The complete MyGymPal planner, ready to download.",
        includes: [
          "Weekly gym schedule & daily session logs",
          "Multi-week program builder",
          "Exercise library & personal records templates",
          "Cardio and mobility logs",
          "Goal-setting pages",
          "Bonus guide: Progressive overload basics",
          "Sample 3, 4 & 5-day training splits",
        ],
        whopUrl: "https://whop.com/fitbersama/mygympal/",
      },
    ],
  },
  {
    slug: "4-week-fat-loss-ebook",
    name: "4-Week Fat Loss eBook",
    tagline: "Four focused weeks of nutrition and training.",
    description:
      "A practical 4-week guide that combines simple nutrition principles with a structured training plan. Clear weekly steps help you build healthier habits at your own pace — no extreme diets, no gimmicks.",
    coverImage: `${IMG}/f13a254259b1ea3e7902de4603acd6d5b68252f387941e68030a58d35d1ea5ea.jpeg`,
    gallery: [`${IMG}/f13a254259b1ea3e7902de4603acd6d5b68252f387941e68030a58d35d1ea5ea.jpeg`],
    formats: PDF_FORMATS,
    highlights: [
      "Week-by-week nutrition guidance",
      "4-week structured training plan",
      "Sample meal ideas and grocery lists",
      "Weekly check-in pages",
    ],
    variants: [
      {
        id: "standard",
        name: "Full Access",
        price: 26,
        description: "The complete 4-Week Fat Loss eBook, ready to download.",
        includes: [
          "4-week training plan",
          "Nutrition fundamentals chapter",
          "Meal planning & grocery list templates",
          "Sample meal ideas",
          "Workout log & weekly check-in pages",
          "Home workout alternatives",
          "Bonus guide: Building habits beyond 4 weeks",
        ],
        whopUrl: "https://whop.com/fitbersama/4-week-fat-losse-book/",
      },
    ],
  },
  {
    slug: "ultimate-fitness-coaching",
    name: "Ultimate Fitness Coaching",
    tagline: "Training, nutrition and progress in one program.",
    description:
      "A complete downloadable coaching program that brings together a structured training program, a practical nutrition guide and progress tracking pages — everything you need to follow a clear plan on your own schedule.",
    coverImage: `${IMG}/a9df45f72b366a1463234f4a239e5bd9bb81f50b2cac70d2b4cfd6e79ec69e93.jpeg`,
    gallery: [`${IMG}/a9df45f72b366a1463234f4a239e5bd9bb81f50b2cac70d2b4cfd6e79ec69e93.jpeg`],
    formats: PDF_FORMATS,
    highlights: [
      "Structured multi-week training program",
      "Practical nutrition guide",
      "Progress tracking and check-in pages",
      "Hyperlinked tabs for fast navigation",
    ],
    variants: [
      {
        id: "standard",
        name: "Full Access",
        price: 30,
        description: "The complete Ultimate Fitness Coaching program, ready to download.",
        includes: [
          "Training program & workout log pages",
          "Nutrition guide & meal planning templates",
          "Measurement & check-in pages",
          "Monthly review pages",
          "Habit tracker & milestone pages",
          "Bonus guide: Recovery, sleep & rest days",
          "Bonus guide: Staying consistent long-term",
        ],
        whopUrl: "https://whop.com/fitbersama/ultimate-fitness-coaching-b9/",
      },
    ],
  },
];

